import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import SocialIcons, { detectPlatform, PLATFORM_CONFIG } from '../../components/SocialIcons';
import siteSettingsService from '../../services/siteSettingsService';
import { API_URL } from '../../config/api';
import './AdminSocialLinksPage.css';

const PLATFORM_OPTIONS = [
  { value: 'facebook',  label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'linkedin',  label: 'LinkedIn' },
  { value: 'twitter',   label: 'Twitter / X' },
  { value: 'youtube',   label: 'YouTube' },
  { value: 'tiktok',    label: 'TikTok' },
  { value: 'whatsapp',  label: 'WhatsApp' },
  { value: 'custom',    label: 'Custom / Other' },
];

const EMPTY_FORM = { name: '', link: '', icon: '', isVisible: true, platform: '' };

const AdminSocialLinksPage = () => {
  const [links, setLinks]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [toast, setToast]       = useState({ msg: '', type: '' });
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);   // null = add, number = edit
  const [form, setForm]         = useState({ ...EMPTY_FORM });
  const [iconPreview, setIconPreview] = useState('');
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const iconInputRef = useRef(null);

  // ── Fetch ────────────────────────────────────────────────
  const fetchLinks = async () => {
    try {
      setLoading(true);
      const data = await siteSettingsService.getSocialLinks();
      setLinks(Array.isArray(data) ? data : []);
    } catch (e) {
      showToast('Failed to load social links', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLinks(); }, []);

  // ── Toast ────────────────────────────────────────────────
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: '' }), 3500);
  };

  // ── Open modal ───────────────────────────────────────────
  const openAdd = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setIconPreview('');
    setShowModal(true);
  };

  const openEdit = (link) => {
    const platform = detectPlatform(link.name) || 'custom';
    setEditingId(link.id);
    setForm({
      name:      link.name || '',
      link:      link.link || '',
      icon:      link.icon || '',
      isVisible: link.isVisible !== false,
      platform,
    });
    setIconPreview(link.icon || '');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setIconPreview('');
  };

  // ── Handle platform select ───────────────────────────────
  const handlePlatformChange = (value) => {
    const label = PLATFORM_OPTIONS.find(p => p.value === value)?.label || '';
    setForm(f => ({
      ...f,
      platform: value,
      name: value !== 'custom' ? label : f.name,
      // Clear icon so built-in SVG is used
      icon: value !== 'custom' ? '' : f.icon,
    }));
    if (value !== 'custom') setIconPreview('');
  };

  // ── Icon upload ─────────────────────────────────────────
  const handleIconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { showToast('Please select an image file', 'error'); return; }
    if (file.size > 2 * 1024 * 1024)    { showToast('Image must be under 2 MB', 'error'); return; }

    setUploadingIcon(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/upload/image?type=logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      const url = data.url || data.imageUrl || '';
      setForm(f => ({ ...f, icon: url }));
      setIconPreview(url);
    } catch {
      showToast('Failed to upload icon', 'error');
    } finally {
      setUploadingIcon(false);
    }
  };

  // ── Save (add / edit) ────────────────────────────────────
  const handleSave = async () => {
    if (!form.name.trim()) { showToast('Platform name is required', 'error'); return; }
    if (!form.link.trim()) { showToast('URL is required', 'error'); return; }

    // Basic URL validation
    try { new URL(form.link); } catch {
      showToast('Enter a valid URL (e.g. https://facebook.com/...)', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name:      form.name.trim(),
        link:      form.link.trim(),
        icon:      form.icon || null,
        isVisible: form.isVisible,
      };

      if (editingId) {
        await siteSettingsService.updateSocialLink(editingId, payload);
        showToast('Social link updated successfully');
      } else {
        await siteSettingsService.createSocialLink(payload);
        showToast('Social link added successfully');
      }
      closeModal();
      fetchLinks();
    } catch {
      showToast('Failed to save. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle visibility ─────────────────────────────────────
  const handleToggle = async (id) => {
    try {
      await siteSettingsService.toggleSocialLinkVisibility(id);
      setLinks(prev => prev.map(l =>
        l.id === id ? { ...l, isVisible: !l.isVisible } : l
      ));
    } catch {
      showToast('Failed to toggle visibility', 'error');
    }
  };

  // ── Delete ────────────────────────────────────────────────
  const handleDelete = async (id) => {
    try {
      await siteSettingsService.deleteSocialLink(id);
      setLinks(prev => prev.filter(l => l.id !== id));
      showToast('Social link deleted');
    } catch {
      showToast('Failed to delete', 'error');
    } finally {
      setDeleteConfirm(null);
    }
  };

  // ── Preview icon for a link ───────────────────────────────
  const LinkIcon = ({ link }) => {
    const platform = detectPlatform(link.name);
    const config   = platform ? PLATFORM_CONFIG[platform] : null;
    return (
      <span
        className="asl-icon-preview"
        style={{
          background: config ? config.bg : '#f0f0f0',
          color:      config ? config.color : '#555',
        }}
      >
        {link.icon ? (
          <img src={link.icon} alt={link.name} />
        ) : config ? (
          React.cloneElement(config.icon, { width: 18, height: 18 })
        ) : (
          <span className="asl-icon-initial">{link.name?.charAt(0)?.toUpperCase()}</span>
        )}
      </span>
    );
  };

  return (
    <AdminLayout>
      {/* Toast */}
      {toast.msg && (
        <div className={`asl-toast asl-toast--${toast.type}`}>{toast.msg}</div>
      )}

      <div className="asl-page">
        {/* Header */}
        <div className="asl-header">
          <div className="asl-header-left">
            <h1 className="asl-title">Social Media Links</h1>
            <p className="asl-subtitle">Manage social links shown in the footer "Follow Us" section</p>
          </div>
          <button className="asl-add-btn" onClick={openAdd}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
            </svg>
            Add New Link
          </button>
        </div>

        {/* Live Preview */}
        {links.filter(l => l.isVisible).length > 0 && (
          <div className="asl-preview-bar">
            <span className="asl-preview-label">Footer preview:</span>
            <SocialIcons
              links={links.filter(l => l.isVisible).map(l => ({
                id: l.id, name: l.name, link: l.link, icon: l.icon
              }))}
              size={34}
              gap={8}
              theme="dark"
            />
          </div>
        )}

        {/* Table */}
        <div className="asl-card">
          {loading ? (
            <div className="asl-loading">
              <div className="asl-spinner"></div>
              <span>Loading social links…</span>
            </div>
          ) : links.length === 0 ? (
            <div className="asl-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#D1D5DB" strokeWidth="1.5"/>
                <path d="M8 12h8M12 8v8" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <p>No social links yet.</p>
              <button className="asl-add-btn" onClick={openAdd}>Add your first link</button>
            </div>
          ) : (
            <table className="asl-table">
              <thead>
                <tr>
                  <th>Platform</th>
                  <th>Name</th>
                  <th>URL</th>
                  <th>Visible</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {links.map(link => (
                  <tr key={link.id} className={link.isVisible ? '' : 'asl-row--hidden'}>
                    <td>
                      <LinkIcon link={link} />
                    </td>
                    <td className="asl-td-name">{link.name}</td>
                    <td className="asl-td-url">
                      <a href={link.link} target="_blank" rel="noopener noreferrer" className="asl-url-link">
                        {link.link}
                      </a>
                    </td>
                    <td>
                      <button
                        className={`asl-toggle ${link.isVisible ? 'asl-toggle--on' : ''}`}
                        onClick={() => handleToggle(link.id)}
                        title={link.isVisible ? 'Click to hide' : 'Click to show'}
                      >
                        <span className="asl-toggle-knob"></span>
                      </button>
                    </td>
                    <td>
                      <div className="asl-actions">
                        <button className="asl-btn-edit" onClick={() => openEdit(link)} title="Edit">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                            <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Edit
                        </button>
                        <button className="asl-btn-delete" onClick={() => setDeleteConfirm(link.id)} title="Delete">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                            <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Add / Edit Modal ──────────────────────────────── */}
      {showModal && (
        <div className="asl-modal-overlay" onClick={closeModal}>
          <div className="asl-modal" onClick={e => e.stopPropagation()}>
            <div className="asl-modal-header">
              <h2>{editingId ? 'Edit Social Link' : 'Add Social Link'}</h2>
              <button className="asl-modal-close" onClick={closeModal}>✕</button>
            </div>

            <div className="asl-modal-body">
              {/* Platform selector */}
              <div className="asl-field">
                <label>Platform</label>
                <div className="asl-platform-grid">
                  {PLATFORM_OPTIONS.map(opt => {
                    const cfg = opt.value !== 'custom' ? PLATFORM_CONFIG[opt.value] : null;
                    const isSelected = form.platform === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        className={`asl-plat-btn ${isSelected ? 'selected' : ''}`}
                        onClick={() => handlePlatformChange(opt.value)}
                        style={isSelected && cfg ? {
                          borderColor: cfg.color,
                          background: cfg.bg,
                          color: cfg.color,
                        } : {}}
                      >
                        {cfg ? (
                          <span style={{ color: cfg.color }}>
                            {React.cloneElement(cfg.icon, { width: 16, height: 16 })}
                          </span>
                        ) : (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/>
                            <path d="M12 8v4l2.5 2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                          </svg>
                        )}
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Name */}
              <div className="asl-field">
                <label>Display Name <span className="asl-req">*</span></label>
                <input
                  className="asl-input"
                  type="text"
                  placeholder="e.g. Facebook"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                />
              </div>

              {/* URL */}
              <div className="asl-field">
                <label>URL <span className="asl-req">*</span></label>
                <input
                  className="asl-input"
                  type="url"
                  placeholder="https://facebook.com/yourpage"
                  value={form.link}
                  onChange={e => setForm(f => ({ ...f, link: e.target.value }))}
                />
              </div>

              {/* Custom icon upload */}
              <div className="asl-field">
                <label>Custom Icon <span className="asl-optional">(optional — uses built-in icon if blank)</span></label>
                <div className="asl-icon-upload-row">
                  <div className="asl-icon-thumb">
                    {iconPreview ? (
                      <img src={iconPreview} alt="icon" />
                    ) : (
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="3" width="18" height="18" rx="3" stroke="#D1D5DB" strokeWidth="1.5"/>
                        <path d="M3 16l5-5 4 4 3-3 4 4" stroke="#D1D5DB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="8.5" cy="8.5" r="1.5" fill="#D1D5DB"/>
                      </svg>
                    )}
                  </div>
                  <label className="asl-upload-btn">
                    {uploadingIcon ? 'Uploading…' : (iconPreview ? 'Replace' : 'Upload Icon')}
                    <input
                      ref={iconInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={handleIconUpload}
                      disabled={uploadingIcon}
                    />
                  </label>
                  {iconPreview && (
                    <button
                      type="button"
                      className="asl-remove-icon"
                      onClick={() => { setIconPreview(''); setForm(f => ({ ...f, icon: '' })); }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>

              {/* Visibility toggle */}
              <div className="asl-field asl-field--row">
                <label>Visible in footer</label>
                <button
                  type="button"
                  className={`asl-toggle ${form.isVisible ? 'asl-toggle--on' : ''}`}
                  onClick={() => setForm(f => ({ ...f, isVisible: !f.isVisible }))}
                >
                  <span className="asl-toggle-knob"></span>
                </button>
                <span className="asl-vis-label">{form.isVisible ? 'Visible' : 'Hidden'}</span>
              </div>
            </div>

            <div className="asl-modal-footer">
              <button className="asl-btn-cancel" onClick={closeModal}>Cancel</button>
              <button className="asl-btn-save" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : (editingId ? 'Save Changes' : 'Add Link')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirmation ───────────────────────────── */}
      {deleteConfirm && (
        <div className="asl-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="asl-modal asl-modal--sm" onClick={e => e.stopPropagation()}>
            <div className="asl-modal-header">
              <h2>Delete Social Link</h2>
              <button className="asl-modal-close" onClick={() => setDeleteConfirm(null)}>✕</button>
            </div>
            <div className="asl-modal-body">
              <p className="asl-confirm-text">
                Are you sure you want to remove this social link? This action cannot be undone.
              </p>
            </div>
            <div className="asl-modal-footer">
              <button className="asl-btn-cancel" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="asl-btn-delete-confirm" onClick={() => handleDelete(deleteConfirm)}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminSocialLinksPage;
